import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import { Crosshair, RotateCcw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

// Same emerald pin styling as the main MapView so it's visually consistent.
const dragIcon = L.divIcon({
    className: "pinpicker-marker",
    html: `<div style="width:28px;height:28px;background:hsl(131 22% 29%);border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 4px 10px rgba(0,0,0,.25);"></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 26],
});

const publicIcon = L.divIcon({
    className: "pinpicker-public-marker",
    html: `<div style="width:22px;height:22px;background:#f5efe4;border:2px dashed hsl(131 22% 29%);border-radius:50%;display:flex;align-items:center;justify-content:center;font:700 12px sans-serif;color:hsl(131 22% 29%);box-shadow:0 2px 6px rgba(0,0,0,.2);">≈</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
});

function Recenter({ lat, lng, fitRadius }) {
    const map = useMap();
    useEffect(() => {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (fitRadius > 0) map.fitBounds(L.latLng(lat, lng).toBounds(fitRadius * 2.2), { animate: true, padding: [24, 24] });
        else map.setView([lat, lng], Math.max(map.getZoom(), 16), { animate: true });
    }, [lat, lng, fitRadius, map]);
    return null;
}

// Maps inside dialogs render grey until Leaflet re-measures the container.
function FixSize() {
    const map = useMap();
    useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 250); return () => clearTimeout(t); }, [map]);
    return null;
}

const FALLBACK_CENTER = [55.8642, -4.2518];

function ClickToPlace({ onPlace }) {
    const map = useMap();
    useEffect(() => {
        const handler = (e) => onPlace(e.latlng.lat, e.latlng.lng);
        map.on("click", handler);
        return () => map.off("click", handler);
    }, [map, onPlace]);
    return null;
}

/**
 * Drag-and-drop pin picker for the Pin Manager.
 * - Drag the marker anywhere.
 * - Click the map to jump the pin to that spot.
 * - "Use my location" button snaps to the browser's geolocation.
 * All three actions fire `onChange({ lat, lng })` — parent debounces & autosaves.
 */
export default function MapPinPicker({ lat, lng, onChange, height = 300, publicSpot = null, fuzzyRadius = 0, onPublicChange, onPublicReset, publicWarnings = [] }) {
    const markerRef = useRef(null);
    const placed = Number.isFinite(lat) && Number.isFinite(lng);
    const validLat = placed ? lat : FALLBACK_CENTER[0];
    const validLng = placed ? lng : FALLBACK_CENTER[1];

    const setFromMarker = () => {
        const m = markerRef.current;
        if (!m) return;
        const p = m.getLatLng();
        onChange({ lat: +p.lat.toFixed(6), lng: +p.lng.toFixed(6) });
    };

    const setFromClick = (la, ln) => {
        onChange({ lat: +la.toFixed(6), lng: +ln.toFixed(6) });
    };

    const useMyLocation = () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => onChange({ lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6) }),
            () => {},
            { enableHighAccuracy: true, timeout: 8000 },
        );
    };

    return (
        <div className="space-y-2" data-testid="pin-map-picker">
            <div className="rounded-xl overflow-hidden border border-stone-300 relative"
                 style={{ height: `${height}px` }}>
                <MapContainer center={[validLat, validLng]} zoom={placed ? 16 : 11}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={true}
                    doubleClickZoom={false}>
                    <TileLayer
                        attribution='&copy; OpenStreetMap contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {placed && (
                        <Marker
                            position={[validLat, validLng]}
                            icon={dragIcon}
                            draggable
                            eventHandlers={{ dragend: setFromMarker }}
                            ref={markerRef}
                        />
                    )}
                    {placed && publicSpot && fuzzyRadius > 0 && (
                        <Circle center={[validLat, validLng]} radius={fuzzyRadius}
                            pathOptions={{ color: "hsl(131 22% 29%)", weight: 1, dashArray: "4 4", fillOpacity: 0.06 }}
                            interactive={false} />
                    )}
                    {placed && publicSpot && (
                        <Marker position={[publicSpot.lat, publicSpot.lng]} icon={publicIcon} draggable
                            eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); onPublicChange?.(+p.lat.toFixed(6), +p.lng.toFixed(6)); } }}>
                            <Tooltip direction="top" offset={[0, -10]} permanent>Public sees it here</Tooltip>
                        </Marker>
                    )}
                    {placed && <Recenter lat={validLat} lng={validLng} fitRadius={publicSpot ? fuzzyRadius : 0} />}
                    <FixSize />
                    <ClickToPlace onPlace={setFromClick} />
                </MapContainer>
            </div>
            {placed && publicSpot && (
                <div className="space-y-1.5" data-testid="pin-map-picker-public-hint">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-[11px] text-muted-foreground">
                            Fuzzy pin: everyone else sees you at the dashed <b>≈</b> marker. Drag it anywhere inside the circle (it stays at least 150m from your real spot).
                            {publicSpot.custom ? " You've placed it yourself." : " Placed automatically."}
                        </p>
                        {publicSpot.custom && (
                            <Button type="button" size="sm" variant="outline" data-testid="pin-map-picker-public-reset"
                                onClick={onPublicReset} className="rounded-full h-7 text-xs">
                                <RotateCcw size={11} className="mr-1" /> Reset to automatic
                            </Button>
                        )}
                    </div>
                    {publicWarnings.map((w) => (
                        <div key={w.code} data-testid={`pin-map-picker-warning-${w.code}`}
                            className="flex items-start gap-1.5 text-[11px] rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1.5">
                            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w.message}
                        </div>
                    ))}
                </div>
            )}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[11px] text-muted-foreground">
                    {placed ? "Drag the pin, or tap anywhere on the map, to nudge it into the exact right spot."
                            : "No address? Tap the map to drop the pin where you were standing."}
                </p>
                <Button type="button" size="sm" variant="outline"
                    data-testid="pin-map-picker-locate"
                    onClick={useMyLocation}
                    className="rounded-full h-7 text-xs">
                    <Crosshair size={11} className="mr-1" /> Use my location
                </Button>
            </div>
        </div>
    );
}
